import { Box, Button, Container, Grid, Link, Paper, Stack, Theme, Typography } from '@mui/material';
import { computed, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import type { RootStore } from '../../stores/RootStore';
import StoreContext from '../../stores/StoreContext';
import { type HookTypes, withHooks } from '../../utils/withHooks';
import CompanyCard from '../common/company/CompanyCard';
import ProfilePicture from '../common/ProfilePicture';
import SingleValueStat from '../common/stats/SingleValueStat';
import './CompanyLanding.scss';
import { DateUtil } from '../../helpers/DateUtil';
import { isUndefined } from 'lodash';
import VideoContainer from '../common/VideoContainer';
import type { Company } from '../../stores/company/Company';

import ReactMarkdown from 'react-markdown';
import CompanyPublicReviews from './CompanyPublicReviews';

type Props = HookTypes & {
	company: Company;
};

const CompanyLanding = observer(
	class CompanyLanding extends React.Component<Props> {
		static readonly contextType = StoreContext;
		get params() {
			return this.props.params!;
		}

		get history() {
			// biome-ignore lint/style/noNonNullAssertion: <explanation>
			return this.props.navigate!;
		}

		tryToLoadDomain: string = '';
		loading: boolean = false;
		companyDetails: any;

		buttonStyle = (theme: Theme) => ({
			color: theme.palette.secondary.contrastText,
			borderRadius: '.5rem',
			letterSpacing: '1px',
			borderWidth: '2px',
			['&:hover']: {
				borderWidth: '2px',
				borderColor: theme.palette.secondary.main,
			},
		});

		constructor(props: Props) {
			super(props);

			makeObservable(this, {
				loading: observable,
				companyDetails: observable,
				employees: computed,
				rootStore: computed,
				workspaceStore: computed,
				companyStore: computed,
				uiState: computed,
				company: computed,
				image: computed,
				elvirksomhetsregisteret: computed,
				elRegLastUpdated: computed,
			});
		}

		componentDidMount(): void {
			this.init().catch(console.error);
		}

		componentDidUpdate(prevProps: Readonly<Props>): void {
			if (prevProps.company?.workspaceId !== this.props.company?.workspaceId) {
				this.init().catch(console.error);
			}
		}

		componentWillUnmount(): void {
			this.destroy();
		}

		get rootStore() {
			return this.context as RootStore;
		}

		get workspaceStore() {
			return this.rootStore.workspaceStore;
		}

		get companyStore() {
			return this.rootStore.companyStore;
		}

		get uiState() {
			return this.rootStore.uiState;
		}

		get profileStore() {
			return this.rootStore.profileStore;
		}

		get elRegLastUpdated() {
			if (this.elvirksomhetsregisteret?.updated) {
				return new Date(this.elvirksomhetsregisteret.updated);
			}

			return undefined;
		}

		get elvirksomhetsregisteret() {
			if (this.company?.elvirksomhetsregisteret) {
				return this.company.elvirksomhetsregisteret;
			}

			return null;
		}

		get company() {
			return this.props.company;
		}

		get employees() {
			// @todo remove this patch
			const usersToRemove = ['Jone Brattland'];
			if (this.companyDetails?.name != 'Norgeseliten AS') {
				usersToRemove.push('Hans Christian Eliassen');
			}
			const employees = this.company?.employees?.filter((p: any) => usersToRemove.indexOf(p.name) < 0) ?? [];

			return employees.map((p: any) => this.profileStore.getProfile(p.userId, this.company?.workspaceId));
		}

		get image() {
			const { workspaceTheme } = this.uiState;

			const imageNumber = Math.floor(Math.random() * 3) + 1;

			switch (imageNumber) {
				case 3:
					return `/illustrations/ads/${workspaceTheme}3.png`;
				case 2:
					return `/illustrations/ads/${workspaceTheme}2.png`;
				default:
					return `/illustrations/ads/${workspaceTheme}1.png`;
			}
		}

		init = async () => {
			this.loading = true;
			this.destroy();
			if (this.company) {
				if (!this.company?.page) {
					await this.company.loadCompanyPage();
				}
				if (this.company?.page?.showReviews) {
					this.company.loadReviews().catch(console.log);
				}

				this.company.loadElvirksomhetsregisteret().catch(console.log);
			}
			this.loading = false;

			this.initChat();
		};

		destroy = () => {
			try {
				if ((window as any).BefareChat) {
					(window as any).BefareChat.destroy();
				}
			} catch (error) {
				console.warn('Failed to destroy chat', error);
			}
		};

		initChat = () => {
			if (this.company?.page?.chatWidgetId) {
				try {
					if ((window as any).BefareChat) {
						console.info('Chat already initializing');
						(window as any).BefareChat.init(this.company?.page?.chatWidgetId, true);
					}
				} catch (error) {
					console.warn('Failed to initialize chat', error);
				}
			}
		};

		renderEmployees() {
			if (!this.company?.page?.showEmployees) {
				return null;
			}
			const employees = this.employees
				.filter((profile) => !isUndefined(profile))
				.map((profile: any) => (
					<Grid item container key={profile.userId} xs={12} sm={4} md={3}>
						<Paper className="employee-card" sx={{ padding: 2 }}>
							<Grid container alignItems="stretch" justifyContent="space-evenly">
								<Grid item xs={4} sm={12} md={12} container direction="column" alignItems="center">
									<ProfilePicture profile={profile} workspaceId={this.company?.workspaceId} />
								</Grid>
								<Grid item xs={8} sm={12} md={12} container direction="column" alignItems="stretch">
									<Typography align="center" variant="h5" component="h2">
										{profile?.name}
									</Typography>
									{profile?.organizationTitle && (
										<Typography
											align="center"
											variant="body2"
											color="textSecondary"
											component="p"
											gutterBottom
										>
											{profile?.organizationTitle}
										</Typography>
									)}
									<div className="spacer"></div>
									<Button
										fullWidth
										style={{ marginTop: '1rem' }}
										variant="contained"
										color="primary"
										onClick={() => this.goToOrderChat(profile.userId)}
									>
										Chat med {profile.firstName}
									</Button>
								</Grid>
							</Grid>
						</Paper>
					</Grid>
				));

			if (employees.length < 1) {
				return null;
			}

			return (
				<Grid item container alignItems="center" justifyContent="flex-start">
					<Grid
						item
						xs={12}
						sm={12}
						md={12}
						direction="column"
						alignItems="stretch"
						justifyContent="flex-start"
					>
						<Typography gutterBottom variant="h5" component="h2">
							Ansatte
						</Typography>
					</Grid>
					<Grid
						className="CompanyLanding__employees"
						item
						container
						xs={12}
						sm={12}
						md={12}
						spacing={2}
						alignItems="stretch"
						justifyContent="flex-start"
					>
						{employees}
					</Grid>
				</Grid>
			);
		}

		onOfferClick = () => {
			this.goToOrderChat(undefined, 'Ønsker pristilbud');
		};

		onInspectionClick = () => {
			this.goToOrderChat(undefined, 'Ønsker befaring');
		};

		goToOrderChat = (attUserId?: string, description?: string) => {
			const attUserIds = attUserId ? [attUserId] : undefined;

			if (this.employees.length > 0) {
				// @todo move this patch to profileStore
				// Moving means refactor of profileStore and most likely mobx performance issues
				runInAction(() => {
					this.employees.forEach((employee: any) => {
						const existingProfileIndex = this.profileStore.userProfiles.findIndex(
							(p: any) => p.userId.toString() === employee.userId.toString()
						);

						if (existingProfileIndex < 0) {
							this.profileStore.userProfiles.push(employee);
						}
					});
				});
			}

			const queryString = description ? `?d=${description}` : '';

			if (this.company?.id) {
				this.history(`/order-chat/${this.company.postalCode}/${this.company.id}${queryString}`, {
					state: {
						companyId: this.company.id,
						attUserIds,
					},
				});
			}
		};

		renderStats() {
			if (!this.company) {
				return null;
			}

			const hours = Math.round(this.company.avgResponseTimeInMin / 6) / 10;
			let responseTime = hours;
			let timeUnit = 'h';
			if (this.company.avgResponseTimeInMin < 60) {
				timeUnit = 'min';
				responseTime = this.company.avgResponseTimeInMin;
			}
			const { page } = this.company;
			if (!page) return null;

			const showEmployees = Boolean(page?.showEmployees && +(page.numEmployees ?? 0) > 0);
			const showResponseTime = Boolean(page?.showResponseTime && responseTime > 0);
			const showRadius = false;
			return (
				<Grid container spacing={0} alignItems="center" justifyContent="space-evenly">
					{Boolean(page?.showCO2Saved) && (
						<Grid item xs={6} sm={4} md={3} direction="column" alignItems="center">
							<SingleValueStat number={this.company.co2SavedInKg} unit="kg" title="CO2 spart" />
						</Grid>
					)}
					{showEmployees && (
						<Grid item xs={6} sm={4} md={3} direction="column" alignItems="center">
							<SingleValueStat number={page.numEmployees} title="Ansatte" />
						</Grid>
					)}
					{showResponseTime && (
						<Grid item xs={6} sm={4} md={3} direction="column" alignItems="center">
							<SingleValueStat number={responseTime} unit={timeUnit} title="Snitt svartid" />
						</Grid>
					)}
					{showRadius && (
						<Grid item xs={6} sm={4} md={3} direction="column" alignItems="center">
							<SingleValueStat number={this.company.radius} unit="km" title="Aksjonsradius" />
						</Grid>
					)}
				</Grid>
			);
		}

		render() {
			if (this.loading) {
				return (
					<Container className="CompanyLanding">
						<Box className="loading">
							<Box className="dot-typing">
								<span></span>
								<span></span>
								<span></span>
							</Box>
						</Box>
					</Container>
				);
			}

			if (!this.company) {
				return null;
			}

			return (
				<Container className="CompanyLanding">
					{!this.loading && this.company?.name && (
						<Stack spacing={2} alignItems="center" justifyContent="space-evenly">
							{this.company && (
								<VideoContainer
									videoUrl={this.company?.page?.videoUrl ?? this.company?.videoUrl}
									videoTitle={
										this.company?.page?.title ?? this.company?.name ?? this.uiState.serviceName
									}
									// @todo BEF-561 change `description` to `slogan`
									videoSubtitle={
										this.company?.page?.slogan ??
										'Få hjelp til elektrikerarbeid, installasjon og vedlikehold av elektriske anlegg.'
									}
									actionButtons={[
										<Button
											key="button-offer"
											variant="contained"
											color="secondary"
											size="large"
											sx={[this.buttonStyle, { boxShadow: '0 2px 4px rgba(0,0,0,.26)' }]}
											onClick={this.onOfferClick}
										>
											Pristilbud
										</Button>,
										<Button
											key="button-free-inspection"
											variant="outlined"
											color="secondary"
											size="large"
											sx={[
												this.buttonStyle,
												(theme) => ({
													color: theme.palette.primary.contrastText,
													borderColor: theme.palette.primary.contrastText,
												}),
											]}
											onClick={this.onInspectionClick}
										>
											Befaring
										</Button>,
									]}
								/>
							)}

							{this.renderStats()}
							<Grid container spacing={2} alignItems="center" justifyContent="space-evenly">
								{this.company?.page?.description && (
									<Grid item xs={12} sm={12} md={12} container direction="column" alignItems="center">
										<Box sx={{ width: 600, maxWidth: '100%' }}>
											<ReactMarkdown
												data-hj-suppress
												skipHtml
												components={{ h1: 'h3', h2: 'h3' }}
											>
												{this.company.page.description}
											</ReactMarkdown>
										</Box>
									</Grid>
								)}
								{this.company?.page?.showReviews && this.company?.reviews?.avgRating && (
									<Grid item xs={12} sm={12} md={12} container direction="column" alignItems="center">
										<CompanyPublicReviews reviews={this.company.reviews} />
									</Grid>
								)}
								<Grid item xs={12} sm={6} md={6} container direction="column" alignItems="center">
									<Box className="contact" bgcolor="primary.main" color="primary.contrastText">
										<Typography gutterBottom variant="h5" component="h2">
											Snakk med oss
										</Typography>
										<Typography gutterBottom variant="body2">
											Hvis du har et drømmeprosjekt i tankene, en jobb som må gjøres eller rett og
											slett et behov for å prate med fagfolk - ta kontakt.
										</Typography>
										<Button
											variant="contained"
											color="secondary"
											style={{ marginTop: '2rem' }}
											fullWidth
											onClick={() => this.goToOrderChat()}
										>
											Snakk med {this.company?.name}
										</Button>
									</Box>
								</Grid>
								<Grid item xs={12} sm={6} md={6} container direction="column" alignItems="center">
									<CompanyCard company={this.company} hideEmailAddress hidePhoneNumber />
								</Grid>

								{this.renderEmployees()}
							</Grid>
						</Stack>
					)}

					{this.elvirksomhetsregisteret?.id && (
						<Typography
							gutterBottom
							variant="overline"
							component={Link}
							target="_blank"
							href={`https://elvirksomhetsregisteret.dsb.no/org/${this.elvirksomhetsregisteret.id}`}
						>
							Registrert i elvirksomhetsregisteret{' '}
							{this.elRegLastUpdated && (
								<i>(sist sjekket {DateUtil.dateToLocaleFormat(this.elRegLastUpdated)})</i>
							)}
						</Typography>
					)}
				</Container>
			);
		}
	}
);

export default withHooks(CompanyLanding);
